import { useState } from "react";
import {
  Play,
  Download,
  Trash2,
  Share2,
  Copy,
  Check,
  RefreshCw,
  Scissors,
  Clock,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";

export default function ClipPlayer({
  clips,
  meetingStatus,
  onDeleteClip,
  onRegenerateClips,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const formatTimestamp = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0)
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleCopyUrl = (clip) => {
    if (!clip.url) return;
    navigator.clipboard.writeText(clip.url);
    setCopiedId(clip.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownload = (clip) => {
    if (!clip.url) return;
    const a = document.createElement("a");
    a.href = clip.url;
    a.download = `${clip.title || "clip"}.mp4`;
    a.click();
  };

  const handleDelete = async (clip) => {
    if (!window.confirm(`Delete this clip?\n"${clip.title}"`)) return;
    setDeletingId(clip.id);
    try {
      await onDeleteClip?.(clip.id);
    } catch {
      // handled upstream
    } finally {
      setDeletingId(null);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await onRegenerateClips?.();
    } catch {
      // handled upstream
    } finally {
      setRegenerating(false);
    }
  };

  // Strategy display
  const strategyLabels = {
    keyword: { label: "Key Insight", color: "#6366f1" },
    qa: { label: "Q&A", color: "#f59e0b" },
    "speaker-energy": { label: "Discussion", color: "#10b981" },
    "topic-transition": { label: "Opening/Closing", color: "#8b5cf6" },
  };

  // AI category display (used when clips are AI-selected)
  const aiCategoryLabels = {
    insight: { label: "Insight", color: "#6366f1" },
    qa: { label: "Q&A", color: "#f59e0b" },
    debate: { label: "Debate", color: "#ef4444" },
    story: { label: "Story", color: "#8b5cf6" },
    quote: { label: "Quote", color: "#06b6d4" },
    energy: { label: "Energy", color: "#f97316" },
    humour: { label: "Humour", color: "#ec4899" },
  };

  // Header with regenerate button
  const canRegenerate =
    meetingStatus === "clips-ready" || meetingStatus === "transcribed";

  return (
    <div>
      {/* Toolbar */}
      {canRegenerate && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: "rgba(99,102,241,0.08)",
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              color: colors.info,
              fontSize: 12,
              fontFamily: fonts.body,
              cursor: regenerating ? "default" : "pointer",
              opacity: regenerating ? 0.6 : 1,
            }}
          >
            <RefreshCw
              size={13}
              className={regenerating ? "spin" : ""}
            />
            {regenerating ? "Regenerating..." : "Regenerate Clips"}
          </button>
        </div>
      )}

      {!clips || clips.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 32,
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          <Scissors
            size={24}
            color={colors.textSecondary}
            style={{ marginBottom: 8, opacity: 0.5 }}
          />
          <div>
            {meetingStatus === "transcribed"
              ? "Clips are being generated..."
              : meetingStatus === "processing"
                ? "Waiting for transcription to complete..."
                : meetingStatus === "uploaded"
                  ? "Process the recording first to generate clips."
                  : "No clips available"}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {clips.map((clip, i) => {
            // Use AI category badge if available, otherwise fall back to strategy
            const badge = clip.aiCategory
              ? aiCategoryLabels[clip.aiCategory] || { label: clip.aiCategory, color: colors.textSecondary }
              : strategyLabels[clip.strategy] || { label: "Clip", color: colors.textSecondary };
            return (
              <div
                key={clip.id || i}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: colors.bgCard,
                }}
              >
                {/* Video player */}
                {clip.url ? (
                  <video
                    controls
                    preload="metadata"
                    style={{
                      width: "100%",
                      display: "block",
                      background: "#000",
                      maxHeight: 240,
                    }}
                  >
                    <source
                      src={clip.url}
                      type={clip.mimeType || "video/mp4"}
                    />
                  </video>
                ) : (
                  <div
                    style={{
                      height: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,0.2)",
                    }}
                  >
                    <Play size={24} color={colors.textSecondary} />
                  </div>
                )}

                {/* Clip info */}
                <div style={{ padding: "10px 14px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.text,
                        }}
                      >
                        {clip.title || `Clip ${i + 1}`}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 4,
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: `${badge.color}18`,
                            color: badge.color,
                            fontWeight: 600,
                          }}
                        >
                          {badge.label}
                        </span>
                        {clip.speaker && clip.speaker !== "unknown" && (
                          <span
                            style={{
                              fontSize: 10,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: "rgba(99,102,241,0.08)",
                              color: colors.info,
                              fontWeight: 600,
                            }}
                          >
                            {clip.speaker}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: 11,
                            color: colors.textSecondary,
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <Clock size={10} />
                          {formatTimestamp(clip.startSeconds)} –{" "}
                          {formatTimestamp(clip.endSeconds)}
                        </span>
                        {clip.durationSeconds && (
                          <span
                            style={{
                              fontSize: 11,
                              fontFamily: fonts.mono,
                              color: colors.textSecondary,
                            }}
                          >
                            ({formatDuration(clip.durationSeconds)})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transcript excerpt */}
                  {clip.transcript && (
                    <div
                      style={{
                        fontSize: 12,
                        color: colors.textSecondary,
                        lineHeight: 1.5,
                        marginTop: 6,
                        padding: 8,
                        background: "rgba(0,0,0,0.02)",
                        borderRadius: 4,
                        maxHeight: 60,
                        overflow: "hidden",
                      }}
                    >
                      "{clip.transcript.substring(0, 200)}
                      {clip.transcript.length > 200 ? "..." : ""}"
                    </div>
                  )}

                  {/* AI reason (shown when clip was AI-selected) */}
                  {clip.aiReason && (
                    <div
                      style={{
                        fontSize: 11,
                        color: colors.textSecondary,
                        fontStyle: "italic",
                        marginTop: 4,
                        padding: "4px 8px",
                      }}
                    >
                      AI: {clip.aiReason}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginTop: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {clip.url && (
                      <>
                        <ActionBtn
                          icon={Download}
                          label="Download"
                          onClick={() => handleDownload(clip)}
                        />
                        <ActionBtn
                          icon={copiedId === clip.id ? Check : Share2}
                          label={copiedId === clip.id ? "Copied!" : "Copy Link"}
                          onClick={() => handleCopyUrl(clip)}
                        />
                      </>
                    )}
                    <ActionBtn
                      icon={Trash2}
                      label={deletingId === clip.id ? "Deleting..." : "Delete"}
                      color={colors.error}
                      onClick={() => handleDelete(clip)}
                      disabled={deletingId === clip.id}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, color, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        background: "none",
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        color: color || colors.textSecondary,
        fontSize: 11,
        fontFamily: fonts.body,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}
